import { ChangeDetectionStrategy, Component, Inject } from "@angular/core";

import {
  confirmationModalServiceInjectionToken, EFieldType,
  EColorScheme,
  ESeverity,
  IConfirmationModalService,
  IInputGroupOptions,
  IReferenceList,
  IThemeService,
  IToastService,
  themeServiceInjectionToken,
  toastServiceInjectionToken,
  InputGroupComponent
} from "@zaeper/angular-dashboard-view-lib";
import { ZTemplateDirective } from "@zaeper/angular-core-lib";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { SelectModule } from "primeng/select";
import {
  userAuthenticationRestClientInjectionToken
} from "../../../../rest-clients/user/authentication/user-authentication-rest-client-injection-token";
import {
  IUserAuthenticationRestClient
} from "../../../../rest-clients/user/authentication/i-user-authentication-rest.client";
import {
  IDeleteAccountPreview
} from "../../../../rest-clients/user/authentication/responses/i-delete-account-preview";
import { HostUtil } from "../../../../utils/host-util";
import {
  sessionStateServiceInjectionToken
} from "../../../../global-states/session/session-state-service-injection-token";
import { ISessionStateService } from "../../../../global-states/session/i-session-state.service";

@Component({
  selector: "app-user-properties-tab",
  templateUrl: "./user-properties-tab.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputGroupComponent,
    ZTemplateDirective,
    ButtonModule,
    CardModule,
    SelectModule
]
})
export class UserPropertiesTabComponent {
  public colorSchemeOptions = [
    { label: "Hell", value: EColorScheme.LIGHT },
    { label: "Dunkel", value: EColorScheme.DARK },
    { label: "System", value: EColorScheme.SYSTEM }
  ];

  public selectedColorScheme: EColorScheme;

  public emailInputGroup: IInputGroupOptions = {
    groupId: "email-settings",
    children: [
      {
        fieldType: EFieldType.TEXT,
        placeholder: "Neue E-Mail Adresse",
        required: true,
        formControlEntry: {
          formControl: new FormControl(""),
          key: "newEmail"
        },
        title: "Neue E-Mail Adresse",
        inputId: "new-email"
      }, {
        fieldType: EFieldType.TEXT,
        placeholder: "Neue E-Mail Adresse bestätigen",
        required: true,
        formControlEntry: {
          formControl: new FormControl(""),
          key: "confirmEmail"
        },
        title: "Neue E-Mail Adresse bestätigen",
        inputId: "email-confirmation"
      }
    ]
  };

  constructor(
    @Inject(confirmationModalServiceInjectionToken) private readonly _confirmationModalService: IConfirmationModalService,
    @Inject(userAuthenticationRestClientInjectionToken) private readonly _userRestClient: IUserAuthenticationRestClient,
    @Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
    @Inject(themeServiceInjectionToken) private readonly _themeService: IThemeService,
    @Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService
  ) {
    this.selectedColorScheme = EColorScheme.SYSTEM;
    this._themeService.colorScheme$.subscribe(scheme => {
      this.selectedColorScheme = scheme;
    });
  }

  public onColorSchemeChange(scheme: EColorScheme): void {
    this._themeService.setColorScheme(scheme);
    this._userRestClient.updateColorSchemePreference(scheme).catch(() => {
      this._toastService.setMessage({
        title: "Fehler",
        detail: "Die Farbeinstellung konnte nicht gespeichert werden.",
        severity: ESeverity.error
      });
    });
  }

  public sendEmailPasswordRequest(): void {
    const currentUser = this._sessionStateService.getCurrentUser();

    if (!currentUser) {
      this._toastService.setMessage({
        title: "Fehler",
        detail: "Benutzerinformationen konnten nicht geladen werden. Bitte versuche es erneut.",
        severity: ESeverity.error
      });
      return;
    }

    this._confirmationModalService.open({
      title: "Passwort Anfrage senden",
      description: `E-Mail zum zurücksenden des Passworts an ${currentUser.email} senden?`,
      confirmationText: "Senden",
      onConfirm: () => this.sendResetPasswordRequest(currentUser.email)
    });
  }

  public async sendDeleteAccountRequest(): Promise<void> {
    try {
      const preview: IDeleteAccountPreview = await this._userRestClient.getDeleteAccountPreview();
      const referenceLists: IReferenceList[] = this._buildReferenceLists(preview);

      this._confirmationModalService.open({
        title: "Konto löschen",
        description: "Bist du sicher, dass du dein Konto unwiderruflich löschen möchtest? Alle deine Daten werden gelöscht und können nicht wiederhergestellt werden.",
        confirmationText: "Konto löschen",
        referenceLists: referenceLists.length > 0 ? referenceLists : undefined,
        onConfirm: () => this._deleteAccount()
      });
    } catch {
      this._toastService.setMessage({
        title: "Fehler",
        detail: "Die Kontolöschung konnte nicht vorbereitet werden. Bitte versuche es erneut.",
        severity: ESeverity.error
      });
    }
  }

  private sendResetPasswordRequest(email: string): Promise<void> {
    return this._userRestClient.sendResetPasswordRequest({
      email: email
    })
      .then(() => {
        this._toastService.setMessage({
          title: "Erfolgreich",
          detail: "Anfrage zum zurücksetzen des Passworts wurde erfolgreich versendet",
          severity: ESeverity.success
        });
      })
      .catch(() => {
        this._toastService.setMessage({
          title: "Fehler bei der Anfrage",
          detail: "Anfrage zum zurücksetzen des Passworts konnte nicht versendet werden",
          severity: ESeverity.error
        });
      });
  }

  private _deleteAccount(): Promise<void> {
    return this._userRestClient.deleteAccount()
      .then(() => {
        window.location.href = HostUtil.getAuthenticationSiteUrl();
      })
      .catch(() => {
        this._toastService.setMessage({
          title: "Fehler",
          detail: "Das Konto konnte nicht gelöscht werden. Bitte versuche es erneut.",
          severity: ESeverity.error
        });
      });
  }

  private _buildReferenceLists(preview: IDeleteAccountPreview): IReferenceList[] {
    const lists: IReferenceList[] = [];

    const otherProducts: string[] = preview.productRegistrations
      .filter((p: string) => p !== "user-dashboard");

    if (otherProducts.length > 0) {
      lists.push({
        description: "Folgende Produktregistrierungen werden entfernt:",
        items: otherProducts.map((p: string) => this._getProductDisplayName(p))
      });
    }

    if (preview.companiesToDelete.length > 0) {
      lists.push({
        description: "Folgende Organisationen werden gelöscht, da du der einzige Administrator bist:",
        items: preview.companiesToDelete.map((c) => c.companyName)
      });
    }

    if (preview.companiesToLeave.length > 0) {
      lists.push({
        description: "Du wirst aus folgenden Organisationen entfernt:",
        items: preview.companiesToLeave.map((c) => c.companyName)
      });
    }

    if (preview.hasActiveSubscriptions) {
      lists.push({
        description: "Aktive Abonnements:",
        items: ["Alle aktiven Abonnements werden sofort gekündigt."]
      });
    }

    return lists;
  }

  private _getProductDisplayName(productId: string): string {
    const displayNames: Record<string, string> = {
      "developer-dashboard": "Developer Dashboard",
      "application-store": "Application Store",
      "management-dashboard": "Management Dashboard"
    };
    return displayNames[productId] || productId;
  }
}
